import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { includes } from 'ramda';
import { environment } from '../../environments/environment.devstage';

interface Permissions {
  roles: string[];
  permissions: string[];
}

/**
 * Der Mandant, dessen Rechtstexte (Impressum, Datenschutzhinweise) gelten.
 * Bewusst ein eigener Begriff und nicht `tenant_reduced_targeting`: das ist ein
 * Fähigkeits-Flag für die Instruction-Zuordnung und nur zufällig deckungsgleich
 * mit FPS. Ein Pflichttext darf nicht an einer Targeting-Einstellung hängen.
 */
export type LegalTenant = 'FGS' | 'FPS';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public Permissions!: Permissions;
  // Variables
  authUrl = environment.apiUrl + 'oauth/token';
  options: any;
  isLoginSubject = new BehaviorSubject<boolean>(this.hasToken());
  /**
   * Constructor
   * @param http The http client object
   */
  constructor(private http: HttpClient) {
    this.options = {
      headers: new HttpHeaders({
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
    };
  }
  /**
   * Get an access token
   * @param e The email address
   * @param p The password string
   */
  login(e: string, p: string) {
    return this.http
      .post(
        this.authUrl,
        {
          grant_type: 'password',
          client_id: environment.oAuthClientId,
          client_secret: environment.oAuthClientSecret,
          username: e,
          password: p,
          scope: '',
        },
        this.options
      )
      .pipe(
        map((data: any) => {
          this.Permissions = {
            permissions: data.permissions,
            roles: data.roles,
          };
          localStorage.setItem('Permissions', JSON.stringify(this.Permissions));
          // Store the access token in the localstorage
          localStorage.setItem('access_token', data.access_token);

          localStorage.setItem('user_id', data.user_id);
          localStorage.setItem('sector_id', data.sector);
          localStorage.setItem('department_id', data.department);
          this.storeTenant(data.tenant);

          //INFO: to enable the privacy policy acceptance, uncomment the following line and replace 'true' with the actual value from the API response
          //localStorage.setItem('privacy_policy_accepted', data.privacy_policy_accepted);
          localStorage.setItem('privacy_policy_accepted', "true");

          this.isLoginSubject.next(true);
          return data;
        })
      );
  }

  /**
   * Get an access token
   * @param e The email address
   * @param p The password string
   */
  loginOneTimePassword(otp: string) {
    return this.http
      .post(
        this.authUrl,
        {
          grant_type: 'one_time_password',
          client_id: environment.oAuthClientId,
          client_secret: environment.oAuthClientSecret,
          one_time_password: otp,
          scope: '',
        },
        this.options
      )
      .pipe(
        map((data: any) => {
          this.Permissions = {
            permissions: data.permissions,
            roles: data.roles,
          };
          localStorage.setItem('Permissions', JSON.stringify(this.Permissions));
          // Store the access token in the localstorage
          localStorage.setItem('access_token', data.access_token);

          localStorage.setItem('user_id', data.user_id);
          localStorage.setItem('sector_id', data.sector);
          localStorage.setItem('department_id', data.department);
          this.storeTenant(data.tenant);

          //INFO: to enable the privacy policy acceptance, uncomment the following line and replace 'true' with the actual value from the API response
          //localStorage.setItem('privacy_policy_accepted', data.privacy_policy_accepted);
          localStorage.setItem('privacy_policy_accepted', "true");
          this.isLoginSubject.next(true);
          return data;
        })
      );
  }
  /**
   * Revoke the authenticated user token
   */
  logout() {
    this.options.headers.Authorization =
      'Bearer ' + localStorage.getItem('access_token');

    this.isLoginSubject.next(false);

    // Clear all session state AFTER the logout request runs — it still needs the
    // token. finalize() fires on both success and error, so nothing (token,
    // permissions, or a previous tenant's reduced-targeting flag) is left behind
    // to leak into the next login. All localStorage keys here are auth/session-
    // scoped — none are meant to survive logout.
    return this.http
      .get(environment.apiUrl + 'api/user/logout', this.options)
      .pipe(finalize(() => localStorage.clear()));

    // return this.http.get(
    //   environment.apiUrl +
    //     (environment.useAzureLogin ? 'logout/azure' : 'api/user/logout'),
    //   this.options
    // );
  }

  private storeTenant(tenant: any) {
    localStorage.setItem('tenant_id', tenant?.id ? String(tenant.id) : '');
    localStorage.setItem('tenant_name', tenant?.name ?? '');
    localStorage.setItem(
      'tenant_reduced_targeting',
      tenant?.reduced_instruction_targeting ? '1' : '0'
    );
  }

  /**
   * The acting tenant, as stored at login. Drives tenant-dependent menu and UI.
   */
  getTenant(): { id: string | null; name: string | null; reducedTargeting: boolean } {
    return {
      id: localStorage.getItem('tenant_id') || null,
      name: localStorage.getItem('tenant_name') || null,
      reducedTargeting: localStorage.getItem('tenant_reduced_targeting') === '1',
    };
  }

  /**
   * Der Mandant, dessen Rechtstexte gelten — die einzige Stelle, die das
   * entscheidet. Footer, Sidebar und Impressum-Seite fragen hier, statt selbst
   * auf den localStorage zu schauen.
   *
   * Zwei Eingaben, in dieser Reihenfolge:
   *
   * 1. Liegt eine Sitzung vor? Ohne Token gilt FGS — das hält die Login-Seite und
   *    einen anonymen Aufruf von `/imprint` verlässlich bei FGS. Bewusst dasselbe
   *    Kriterium, das die übrige Anwendung verwendet (`isLoginSubject` startet
   *    ebenfalls auf `hasToken()`), damit die Rechtstexte nicht von der sonst
   *    gerenderten Oberfläche abweichen.
   * 2. Der Mandantenname aus der Auth-Antwort. Bewusst der Name und nicht
   *    `tenant_id`: die ID ist ein Auto-Increment-Wert, den nur die FGS-Zeile per
   *    Migration erhält, während die FPS-Zeile je Umgebung entsteht. Die
   *    Backfill-Migration keyt aus demselben Grund auf `name`.
   *
   * FGS ist der Standardzweig. Ein unbekannter oder leerer Name ergibt damit
   * deterministisch FGS statt eines undefinierten Zustands.
   *
   * Bekannte Einschränkung: eine nicht beendete Sitzung (Tab geschlossen statt
   * abgemeldet) hinterlässt Token und Mandanten im localStorage, sodass die
   * Login-Seite bis zum Ablauf des Tokens die Texte dieses Mandanten zeigt. Das
   * ist bewusst getragen und entspricht dem übrigen Verhalten der Anwendung, die
   * in diesem Zustand ohnehin die angemeldete Oberfläche rendert.
   */
  getLegalTenant(): LegalTenant {
    if (!this.hasToken()) {
      return 'FGS';
    }

    // Normalisiert vergleichen: der Name wird beim Provisionieren als freies
    // Argument vergeben (tenant:provision {name}, firstOrCreate auf name), eine
    // abweichende Schreibweise wuerde sonst still im FGS-Zweig landen.
    const name = (localStorage.getItem('tenant_name') ?? '').trim().toUpperCase();

    if (name === 'FPS') {
      return 'FPS';
    }

    if (name !== 'FGS') {
      // Der Standardzweig ist gewollt, aber er darf nicht unsichtbar sein: hier
      // sieht ein angemeldeter Nutzer die Rechtstexte eines anderen Mandanten.
      console.error(
        '[Rechtstexte] Mandant nicht bestimmbar, Standardzweig FGS greift. Gelesener Wert:',
        JSON.stringify(localStorage.getItem('tenant_name'))
      );
    }

    return 'FGS';
  }

  /**
   * Pfad des Datenschutz-Dokuments des geltenden Mandanten. Liegt hier und nicht
   * in den Komponenten, damit jeder Dateiname genau einmal im Code steht.
   */
  getDatenschutzPfad(): string {
    return this.getLegalTenant() === 'FPS'
      ? 'assets/datenschutz-fps-2026-07.pdf'
      : 'assets/datenschutz-fgs-2025-12.pdf';
  }

  /**
   * True when the acting tenant (e.g. FPS) uses the reduced instruction-targeting
   * model — used to hide FGS-only menu items and UI.
   */
  isReducedTenant(): boolean {
    return localStorage.getItem('tenant_reduced_targeting') === '1';
  }

  public hasToken(): boolean {
    return !!localStorage.getItem('access_token');
  }

  checkRole(role: string) {
    if (!this.Permissions) {
      const pdata = localStorage.getItem('Permissions')
        ? localStorage.getItem('Permissions')
        : '';
      this.Permissions = pdata !== null ? JSON.parse(pdata) : null;
    }
    return includes(role, this.Permissions.roles);
  }

  getIsLoginSubject(): BehaviorSubject<boolean> {
    return this.isLoginSubject;
  }
}
